import { useState } from 'react';
import { Phone, Save, AlertTriangle, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { toast } from '@/stores/toast-store';
import { formatDateTime } from '@/lib/utils';
import {
  usePlatformSmsConfig,
  useUpdatePlatformSmsConfig,
  type PlatformSmsConfig,
} from '@/hooks/use-platform-sms-config';

function normalizePreview(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (input.startsWith('+') && digits.length >= 11) return `+${digits}`;
  return input;
}

function isValidLooseE164(input: string): boolean {
  if (!input.trim()) return true;
  const digits = input.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

export function AdminPhoneNumbersPage() {
  const { data, isLoading, error } = usePlatformSmsConfig();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Phone Numbers"
          description="Platform SMS sender numbers used for transactional messages across all tenants."
        />
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Phone Numbers"
          description="Platform SMS sender numbers used for transactional messages across all tenants."
        />
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load platform SMS config. Confirm you are signed in as a super-admin.
        </div>
      </div>
    );
  }

  return (
    <PlatformSmsConfigForm
      key={data?.updatedAt ?? 'empty'}
      saved={data ?? { primarySmsNumber: null, backupSmsNumber: null, updatedAt: null, updatedBy: null }}
    />
  );
}

function PlatformSmsConfigForm({ saved }: { saved: PlatformSmsConfig }) {
  const update = useUpdatePlatformSmsConfig();

  const [primary, setPrimary] = useState(saved.primarySmsNumber ?? '');
  const [backup, setBackup] = useState(saved.backupSmsNumber ?? '');

  const primaryValid = isValidLooseE164(primary);
  const backupValid = isValidLooseE164(backup);
  const dirty = primary !== (saved.primarySmsNumber ?? '') || backup !== (saved.backupSmsNumber ?? '');
  const canSave = dirty && primaryValid && backupValid && !update.isPending;

  async function handleSave() {
    if (!canSave) return;
    const ok = await update
      .mutateAsync({
        primarySmsNumber: primary.trim() === '' ? null : primary.trim(),
        backupSmsNumber: backup.trim() === '' ? null : backup.trim(),
      })
      .then(() => true)
      .catch(() => false);
    if (ok) toast.success('Platform SMS numbers saved');
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Phone Numbers"
        description="Platform SMS sender numbers used for transactional messages (welcome, order confirmation, password reset) across all tenants."
      />

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-sm text-amber-800">
          These numbers must be provisioned and verified in AWS End User Messaging (or the configured provider).
          pos-backend picks up changes within ~30s. Leave a field blank to fall back to the service-level{' '}
          <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">SMS_ORIGINATION_NUMBER</code> env var. Per-location{' '}
          <strong>campaign</strong> sender overrides are configured on the Businesses tab.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-white p-6 space-y-6 max-w-2xl">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Phone className="h-4 w-4" />
          <span>Super-admin only · Affects all tenants</span>
        </div>

        <div className="space-y-2">
          <label htmlFor="primary-sms" className="block text-sm font-medium text-gray-800">
            Primary SMS number
          </label>
          <input
            id="primary-sms"
            type="tel"
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            placeholder="+16808482153"
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
              primaryValid
                ? 'border-border focus:border-primary focus:ring-primary/30'
                : 'border-destructive focus:border-destructive focus:ring-destructive/30'
            }`}
          />
          <p className="text-xs text-muted-foreground">
            E.164 format (e.g. <code>+16808482153</code>) or 10-digit US (e.g. <code>6808482153</code>) —
            auto-normalized to {primary.trim() ? <code>{normalizePreview(primary)}</code> : <code>—</code>}.
          </p>
          {!primaryValid && <p className="text-xs text-destructive">Enter a valid US E.164 number or leave blank.</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="backup-sms" className="block text-sm font-medium text-gray-800">
            Backup SMS number
          </label>
          <input
            id="backup-sms"
            type="tel"
            value={backup}
            onChange={(e) => setBackup(e.target.value)}
            placeholder="+16808480447"
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
              backupValid
                ? 'border-border focus:border-primary focus:ring-primary/30'
                : 'border-destructive focus:border-destructive focus:ring-destructive/30'
            }`}
          />
          <p className="text-xs text-muted-foreground">
            Used automatically if the primary number's send fails. Normalized to{' '}
            {backup.trim() ? <code>{normalizePreview(backup)}</code> : <code>—</code>}.
          </p>
          {!backupValid && <p className="text-xs text-destructive">Enter a valid US E.164 number or leave blank.</p>}
        </div>

        <div className="flex items-center justify-between border-t border-border pt-4">
          <p className="text-xs text-muted-foreground">
            {saved.updatedAt ? `Last updated ${formatDateTime(saved.updatedAt)}` : 'No saved values yet'}
          </p>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
